'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { useCustomRouter, useSetAuthToken } from '@/root/shared-hooks'
import { AUTH_ERROR_CODES, AUTH_ERROR_MESSAGES } from '@/root/shared-types/api/auth'
import type { AuthError } from '@/root/shared-types/api/auth'
import { getChallenge, verifyWebEid } from '../api/webEidService'
import type { WebEidAuthToken } from '../api/webEidService'

const webEidToAuthCode: Record<string, string> = {
  ERR_WEBEID_EXTENSION_UNAVAILABLE: AUTH_ERROR_CODES.WEBEID_EXTENSION_UNAVAILABLE,
  ERR_WEBEID_NATIVE_UNAVAILABLE: AUTH_ERROR_CODES.WEBEID_NATIVE_UNAVAILABLE,
  ERR_WEBEID_USER_TIMEOUT: AUTH_ERROR_CODES.WEBEID_USER_TIMEOUT,
  ERR_WEBEID_CONTEXT_INSECURE: AUTH_ERROR_CODES.WEBEID_CONTEXT_INSECURE,
  ERR_WEBEID_ACTION_PENDING: AUTH_ERROR_CODES.WEBEID_ACTION_PENDING,
  ERR_WEBEID_NATIVE_FATAL: AUTH_ERROR_CODES.WEBEID_NATIVE_FATAL,
  ERR_WEBEID_VERSION_MISMATCH: AUTH_ERROR_CODES.WEBEID_VERSION_MISMATCH,
  ERR_WEBEID_ACTION_TIMEOUT: AUTH_ERROR_CODES.WEBEID_USER_TIMEOUT,
}

export const parseWebEidError = (err: unknown): AuthError | null => {
  if (err !== null && typeof err === 'object' && 'code' in err) {
    const code = (err as { code: string }).code

    if (code === 'ERR_WEBEID_USER_CANCELLED') return null

    // Only treat ERR_WEBEID_* codes as Web eID errors (AxiosError also has 'code')
    const mappedCode = webEidToAuthCode[code]
    if (mappedCode) {
      return { message: AUTH_ERROR_MESSAGES.AUTH_ERROR, code: mappedCode }
    }
  }
  return { message: AUTH_ERROR_MESSAGES.UNKNOWN_ERROR, code: AUTH_ERROR_CODES.UNKNOWN }
}

const authenticateWithWebEid = async (nonce: string, lang: string): Promise<WebEidAuthToken> => {
  const { authenticate } = await import('@web-eid/web-eid-library/web-eid')
  return authenticate(nonce, { lang }) as Promise<WebEidAuthToken>
}

interface UseIdKaartAuthOptions {
  onSuccess?: (accessToken: string) => void | Promise<void>
}

export const useIdKaartAuth = (options?: UseIdKaartAuthOptions) => {
  const { saveToken } = useSetAuthToken()
  const { push, locale } = useCustomRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<AuthError | null>(null)

  const stateRef = useRef<'idle' | 'loading'>('idle')
  const cancelRef = useRef<{ canceled: boolean } | null>(null)
  const onSuccessRef = useRef(options?.onSuccess)

  useEffect(() => {
    onSuccessRef.current = options?.onSuccess
  }, [options?.onSuccess])

  useEffect(() => {
    return () => {
      if (cancelRef.current) cancelRef.current.canceled = true
      stateRef.current = 'idle'
    }
  }, [])

  const logInWithIdKaart = useCallback(
    async (rememberMe = false) => {
      if (stateRef.current !== 'idle') return
      stateRef.current = 'loading'

      const cancelToken = { canceled: false }
      cancelRef.current = cancelToken

      setIsLoading(true)
      setError(null)

      try {
        const {
          data: { nonce, challengeToken },
        } = await getChallenge()
        if (cancelToken.canceled) return

        const authToken = await authenticateWithWebEid(nonce, locale ?? 'et')
        if (cancelToken.canceled) return

        const { data } = await verifyWebEid(authToken, challengeToken)
        if (cancelToken.canceled) return

        if (onSuccessRef.current) {
          await saveToken(data.token, rememberMe)
          await onSuccessRef.current(data.token)
        } else {
          await saveToken(data.token, rememberMe)
          await push(`/${locale}/my/details`)
        }
      } catch (err) {
        if (cancelToken.canceled) return
        const authError = parseWebEidError(err)
        setError(authError)
      } finally {
        if (!cancelToken.canceled) {
          stateRef.current = 'idle'
          setIsLoading(false)
        }
      }
    },
    [locale, push, saveToken]
  )

  return { logInWithIdKaart, isLoading, error }
}
